import { Box } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { CustomFieldProps } from '../../../interfaces/CustomFieldProps';
import { DynamicField } from '../../../interfaces/DynamicField';
import { DynamicFieldResponse } from '../../../interfaces/DynamicFieldResponse';
import DescriptionTextField from '../../customer/reports/finalReport/common/DescriptionField';
import FieldHeader from '../../customer/reports/finalReport/common/FieldHeader';
import TitleField from '../../customer/reports/finalReport/common/TitleField';
import { fieldTypes } from '../../customer/settings/finalReport/templateDialogs/common/fieldTypes';
import { TextStyleExtended } from '../editor/extensions/FontSize';

interface GeneratedFieldsProps {
  customValues: DynamicFieldResponse[];
}

interface SmallTextFieldProps {
  info: DynamicField;
  rootIndex: number;
  siblingIndex?: number;
  isSibling?: boolean;
}

const GeneratedFields = ({ customValues }: GeneratedFieldsProps) => {
  const intl = useIntl();
  const customFieldValues = customValues.map((field) => {
    const fieldSiblings = field.siblings?.map((sibling) => {
      return {
        id: sibling.id,
        fieldId: uuidv4(),
        type: sibling.type,
        value: sibling.value ?? '',
        fieldTitle: sibling.label,
        placeholder: sibling.placeholder,
        isMandatory: sibling.is_mandatory,
        dropdownItems: sibling.dropdown_choices,
      };
    });

    const parentField: DynamicField = {
      id: field.id,
      fieldId: uuidv4(),
      type: field.type,
      value: field.value ?? '',
      fieldTitle: field.label,
      placeholder: field.placeholder,
      isMandatory: field.is_mandatory,
      dropdownItems: field.dropdown_choices,
      siblings: fieldSiblings,
    };

    return parentField;
  });

  const inputStyles = {
    height: '3.5rem',
    overflow: 'hidden',
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
    border: '',
    borderRadius: '6px',
  };

  const inputFieldStyle = {
    height: '46px',
    borderRadius: '6px',
    margin: '4px 0px',
    border: '1px solid transparent',
    '&:focus-within': {
      border: '1px solid black',
    },
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.0275)',
    },
    '& .MuiFilledInput-root.Mui-disabled': {
      backgroundColor: '#FCFCFC',
    },
    '& .MuiInputBase-input.MuiFilledInput-input.Mui-disabled': {
      color: '#181818',
      '-webkit-text-fill-color': '#181818',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
  };

  const multiLineInputFieldStyle = {
    minHeight: '280px',
    maxHeight: '480px',
    borderRadius: '6px',
    margin: '4px 0px',
    border: '1px solid transparent',
    backgroundColor: 'rgba(0, 0, 0, 0.0275)',
    display: 'flex',
    '&:focus-within': {
      border: '1px solid black',
    },
    '& .MuiFilledInput-root:after': {
      border: 'none',
    },
    '& .MuiFilledInput-root:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.0275)',
    },
    '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before': {
      borderBottom: 'none',
    },
  };

  const SmallTextField = ({ info }: SmallTextFieldProps) => (
    <Box flexGrow={1} marginY={1} maxWidth='460px'>
      <FieldHeader title={info.fieldTitle} isMandatory={info.isMandatory} />
      <TextField
        variant='filled'
        hiddenLabel
        fullWidth
        placeholder={info.placeholder}
        defaultValue={info.value}
        sx={inputFieldStyle}
        InputProps={{
          disableUnderline: true,
          sx: {
            ...inputStyles,
            fontSize: 16,
          },
        }}
        disabled
      />
    </Box>
  );

  const DateField = ({ field }: CustomFieldProps) => (
    <Box marginBottom='24px' maxWidth='460px'>
      <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {field.value ? (
          <DatePicker
            label={intl.formatMessage({ defaultMessage: 'Selected date' })}
            disabled
            defaultValue={dayjs(field.value)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'filled',
                sx: {
                  height: '50px',
                  marginTop: '12px',
                  borderRadius: '6px',
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.0275)',
                    border: '1px solid transparent',
                  },
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                  '& .MuiFilledInput-root.Mui-focused': {
                    border: '1px solid black',
                  },
                  '& .MuiFilledInput-root.Mui-disabled': {
                    backgroundColor: '#FCFCFC',
                  },
                  '& .MuiInputBase-input.MuiFilledInput-input.Mui-disabled': {
                    color: '#181818',
                    '-webkit-text-fill-color': '#181818',
                  },
                  '& .MuiFilledInput-root.Mui-error': {
                    border: '1px solid red',
                  },
                },
                size: 'small',
                InputProps: {
                  disableUnderline: true,
                  style: {
                    borderRadius: '6px',
                  },
                },
              },
            }}
          />
        ) : (
          <DatePicker
            label={intl.formatMessage({ defaultMessage: 'Selected date' })}
            disabled
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'filled',
                sx: {
                  height: '50px',
                  marginTop: '12px',
                  borderRadius: '6px',
                  '& .MuiFilledInput-root:after': {
                    border: 'none',
                  },
                  '& .MuiFilledInput-root:before': {
                    borderBottom: 'none',
                  },
                  '& .MuiFilledInput-root': {
                    backgroundColor: 'rgba(0, 0, 0, 0.0275)',
                    border: '1px solid transparent',
                  },
                  '& .MuiFilledInput-root:hover:not(.Mui-disabled,.Mui-error):before':
                    {
                      borderBottom: 'none',
                    },
                  '& .MuiFilledInput-root.Mui-focused': {
                    border: '1px solid black',
                  },
                  '& .MuiFilledInput-root.Mui-error': {
                    border: '1px solid red',
                  },
                },
                size: 'small',
                InputProps: {
                  disableUnderline: true,
                  style: {
                    borderRadius: '6px',
                  },
                },
              },
            }}
          />
        )}
      </LocalizationProvider>
    </Box>
  );

  const BigTextField = ({ field }: CustomFieldProps) => {
    const extensions = [
      StarterKit,
      Underline,
      TextStyleExtended,
      Placeholder.configure({
        placeholder: field.placeholder,
      }),
    ];
    const initialEditor = useEditor({
      extensions: extensions,
      content: field.value,
      autofocus: false,
      editable: false,
    });
    if (!initialEditor) {
      return <></>;
    }

    return (
      <Box marginBottom='24px'>
        <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
        <Box sx={multiLineInputFieldStyle}>
          <EditorContent editor={initialEditor} />
        </Box>
      </Box>
    );
  };

  const DropdownField = ({ field }: CustomFieldProps) => {
    const selectedItem = field.value;

    return (
      <Box flexGrow={1} marginBottom='24px' maxWidth='460px'>
        <FormControl
          fullWidth
          hiddenLabel
          sx={{
            '& .MuiInputBase-root.Mui-disabled': {
              backgroundColor: '#FCFCFC',
            },
          }}
        >
          <FieldHeader
            title={field.fieldTitle}
            isMandatory={field.isMandatory}
          />
          <Select
            variant='filled'
            disableUnderline
            displayEmpty
            sx={inputFieldStyle}
            value={selectedItem}
            disabled
          >
            <MenuItem disabled value={''}>
              <FormattedMessage defaultMessage='Select item' />
            </MenuItem>
            {field.dropdownItems.map((item, itemIndex) => (
              <MenuItem value={item} key={itemIndex}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const generateField = (field: DynamicField, index: number) => {
    const fieldInfo = field;
    switch (fieldInfo.type) {
      case fieldTypes.title:
        return <TitleField key={fieldInfo.fieldId} field={fieldInfo} />;
      case fieldTypes.descriptionText:
        return (
          <DescriptionTextField key={fieldInfo.fieldId} field={fieldInfo} />
        );
      case fieldTypes.datePicker:
        return (
          <DateField key={fieldInfo.fieldId} field={fieldInfo} index={index} />
        );
      case fieldTypes.dropdown:
        return (
          <DropdownField
            key={fieldInfo.fieldId}
            field={fieldInfo}
            index={index}
          />
        );
      case fieldTypes.bigTextField:
        return (
          <BigTextField
            key={fieldInfo.fieldId}
            field={fieldInfo}
            index={index}
          />
        );
      case fieldTypes.smallTextField:
        return (
          <Stack
            key={fieldInfo.fieldId}
            direction='row'
            justifyContent='space-between'
            spacing={1}
            width='100%'
            marginBottom='24px'
          >
            <SmallTextField info={fieldInfo} rootIndex={index} />
            {fieldInfo.siblings &&
              fieldInfo.siblings.map((sibling, siblingIndex) => (
                <SmallTextField
                  key={sibling.fieldId}
                  info={sibling}
                  siblingIndex={siblingIndex}
                  rootIndex={index}
                  isSibling
                />
              ))}
          </Stack>
        );
      default:
        return <></>;
    }
  };
  return (
    <>
      {customFieldValues.map((field, index) => (
        <Box key={index}>{generateField(field, index)}</Box>
      ))}
    </>
  );
};

export default GeneratedFields;
